import * as React from 'react';
import { Box } from '@chakra-ui/react';
import { HStack, Text, VStack } from '@ayble-health/dashboard-components';
import { ChangeBadge, type ReleaseDate } from './ChangeBadge';

type Entry = {
  kind: 'new' | 'updated';
  /** Where it lives, e.g. `ui/pill-toggle-group.tsx`. */
  path: string;
  name: string;
  detail: string;
};

type Release = {
  /** A pinned literal from `RELEASE_DATES` — never "the current release". */
  date: ReleaseDate;
  summary: string;
  entries: Entry[];
};

/**
 * What changed in the system, and when.
 *
 * The rest of the audit renders the *current* state, which makes it a good
 * reference and a poor hand-off document: a reader can't tell what landed
 * yesterday from what has been stable for a year. Entries here are dated and
 * mirrored by a badge on the matching specimen, so a change is findable from
 * either direction.
 *
 * Add a release at the top when a change set ships, using a literal date added
 * to `RELEASE_DATES` — see the standard documented there, and pin the same
 * literal on each specimen badge you add. Keep entries to things a consumer of
 * the system would need to know — a new component, a changed prop contract, a
 * vocabulary that moved — not every internal refactor.
 */
const RELEASES: Release[] = [
  {
    date: 'Aug 24, 2026',
    summary:
      'Tag is retired from feature code. Its two remaining call sites moved to Chip, and the pressable pill it existed for became its own component built on the package Pressable. Two patches to the local Chip. No pixels moved except one deliberate half-pixel.',
    entries: [
      {
        kind: 'new',
        path: 'ui/pressable-pill.tsx',
        name: 'PressablePill',
        detail:
          'The interactive counterpart to Chip. Chip is deliberately inert — cursor: default, user-select: none, and a div root — so a filter pill could not be one; it was built out of <Tag as="button"> instead. This renders the package Pressable (the same primitive Drawer uses) with Chip-shaped chrome, giving a real button with aria-pressed. Chrome is matched to Chip by construction and by measurement rather than by shared code, because Chip is a slot recipe that cannot be handed to a button.',
      },
      {
        kind: 'updated',
        path: 'ui/pill-toggle-group.tsx',
        name: 'PillToggleGroup',
        detail:
          'Now composes PressablePill instead of <Tag as="button">. The group keeps the selection logic; the pill owns how a pill looks. Rendering is unchanged — both states were measured against the Tag build and match exactly — so no prototype using it needs revisiting.',
      },
      {
        kind: 'new',
        path: 'DesignSystem/BlockCardAudit.tsx',
        name: 'Block card (documentation)',
        detail:
          'The Care Guide lane card, documented under Patterns with all six status states, the progress-row vocabulary, and the result chips — rendered from live tokens and the real Chip and Button atoms rather than pasted screenshots, so a specimen cannot drift from what a consumer would get. Records the gap against production, which ships one of the six and styles it differently. Five states are Bucket 2 on one skeleton; Recommended is Bucket 3.',
      },
      {
        kind: 'updated',
        path: 'ui/badge.tsx',
        name: 'Badge',
        detail:
          'positive and negative pass through from the package unchanged. neutral and outline are the two square careOS Tag variants, re-homed onto the square component when Tag was retired — Tag was a pill set and those two were the odd members of it. Documented here for the first time; the specimen previously cited a release that was never in the changelog.',
      },
      {
        kind: 'updated',
        path: 'ui/chip.tsx',
        name: 'Chip',
        detail:
          'Typography now reaches the label. Chip renders a Chakra TagRoot wrapping a TagLabel, and the label slot sets its own font-size and line-height, so fontSize on the root styled the box while the text stayed 12px — meaning a Chip could not be sized below its default at all. fontSize, lineHeight, and letterSpacing are forwarded to the label when a caller passes them; a Chip that passes none is unchanged. Written up as proposal 5 in UPSTREAM_PROPOSALS.md.',
      },
      {
        kind: 'updated',
        path: 'ui/tag.tsx',
        name: 'Tag',
        detail:
          'Retired from feature code. The last two call sites were the Care roster’s Cancelled and New enrollment badges, now Chips. The file stays only because this audit renders Tag to document it — PillToggleGroup, the other thing that held it open, no longer uses it. Do not reach for Tag in new work.',
      },
    ],
  },
  {
    date: 'Aug 13, 2026',
    summary:
      'Care tab rounds 3 & 4 — Apply-gated filters, pagination, an Outcome Goal column, and a reconciliation that collapsed two member-status vocabularies into one. Two new ui/ components and three changed prop contracts.',
    entries: [
      {
        kind: 'new',
        path: 'ui/StatusIndicatorPill.tsx + ui/StatusIndicatorIcons.tsx',
        name: 'StatusIndicatorPill',
        detail:
          'Icon+label status pill, extracted from the patient profile’s GutCheckAppStatus so it and the Care roster’s new CareCoordinationPill share one fg/tint/icon vocabulary instead of each inventing its own colors — the "third surface needs member status" case the Status callout below used to warn about. Deliberately not a Chakra recipe: it renders through the raw CSS-custom-property layer (careos-prototype.css) the careOS surfaces already use, so consumers need a .careos-root ancestor for its var() colors to resolve.',
      },
      {
        kind: 'new',
        path: 'ui/pagination.tsx',
        name: 'Pagination',
        detail:
          'Page-number pagination — PaginationRoot / Items / PageButton / PrevTrigger / NextTrigger. A thin composition over Chakra v3’s own built-in Pagination with no bespoke recipe; the chrome comes from the project Button so a page item reads as the same object as every other button.',
      },
      {
        kind: 'updated',
        path: 'ui/button.tsx',
        name: 'Button',
        detail:
          'The `link` variant set no explicit hover background, so Chakra’s underlying recipe default bled through as a near-black fill under dark text — illegible. Now pinned to a transparent fill with a seafoam outline on hover and a filled seafoam pressed state, with the border reserved at rest so hovering doesn’t shift layout. Affects all five variant="link" call sites.',
      },
      {
        kind: 'updated',
        path: 'ProviderHome/StatusPill.tsx',
        name: 'StatusPill',
        detail:
          'New optional `size` prop (`md` default, `sm`). `sm` is for tight tabular contexts — the Care roster fits the confirmation pill on the same line as an appointment’s date and time, where the full-size pill wraps. Default is unchanged, so provider Home renders exactly as before. Also now used by the Care roster, replacing an inline "· Unconfirmed" text suffix.',
      },
      {
        kind: 'updated',
        path: 'DesignSystem/ChangeBadge.tsx',
        name: 'ChangeBadge',
        detail:
          '`date` is now required and typed to the known release list, replacing a default that meant "the current release". That default silently re-dated every existing badge whenever a new release shipped — a badge is a historical claim, so it has to keep saying what it said. Dates are pinned literals from RELEASE_DATES going forward.',
      },
      {
        kind: 'updated',
        path: 'Form/Input.js',
        name: 'Input (legacy Form/)',
        detail:
          'The clearable "×" now renders only when there is text to clear, instead of sitting in an empty field offering to clear nothing. Affects every isClearable call site.',
      },
    ],
  },
  {
    date: 'Aug 8, 2026',
    summary:
      'CareOS Filters Redesign — the Care tab roster moved its filters into a left panel. Most of the work was feature-level, but it added one component to ui/ and changed three shared prop contracts.',
    entries: [
      {
        kind: 'new',
        path: 'ui/pill-toggle-group.tsx',
        name: 'PillToggleGroup',
        detail:
          'Row of pressable filter pills with selected/unselected state — the first interactive pill control in ui/. Composes the Tag recipe for chrome (seafoam on, canvas off) rather than inventing pill styles. Multi-select by default; single-select mode clears when you click the active pill.',
      },
      {
        kind: 'updated',
        path: 'ui/tag.tsx',
        name: 'Tag',
        detail:
          'Prop type widened to accept the button-only attributes (type, disabled) so it can render polymorphically as a real <button> via `as`. No visual change. Also documented in Atoms for the first time — Tag had been in the registry but never in this audit.',
      },
      {
        kind: 'updated',
        path: 'Form/Select.js',
        name: 'Select (legacy Form/)',
        detail:
          'New optional `styles` and `components` props passed through to react-select. Styles merge onto the project defaults rather than replacing them, so a caller can restyle one slot without redeclaring the rest. Its JSDoc prop list is now complete — a partial one silently becomes the whole prop type and breaks every other call site.',
      },
      {
        kind: 'updated',
        path: 'Form/Input.js',
        name: 'Input (legacy Form/)',
        detail:
          'New optional `borderColor` prop, mirroring the one Form/Select already exposed. Default is unchanged.',
      },
      {
        kind: 'updated',
        path: 'models/user.ts + PatientProfile/RiskDots.tsx',
        name: 'RiskLevel',
        detail:
          'Was declared twice under one name with different members, so which type you got depended on the import path. The model now owns the three levels (low / moderate / high) and RiskDots re-exports them. "Not yet rated" is null at the field, not a member of the type — consumers render the N/A case themselves.',
      },
    ],
  },
];

// Takes the date from its release rather than letting the badge fall back to
// one: an entry under an older release has to carry that release's date, not
// whatever shipped most recently.
const EntryRow = ({ entry, date }: { entry: Entry; date: ReleaseDate }) => (
  <Box borderTopWidth="1px" borderColor="cloud" pt={3}>
    <VStack gap={1} align="stretch">
      <HStack gap={2} align="center" wrap="wrap">
        <Text my={0} fontSize="sm" fontWeight="bold" color="muted.900">
          {entry.name}
        </Text>
        <ChangeBadge kind={entry.kind} date={date} />
        <Text my={0} fontSize="2xs" color="muted.400" fontFamily="mono">
          {entry.path}
        </Text>
      </HStack>
      <Text my={0} fontSize="sm" color="muted.600">
        {entry.detail}
      </Text>
    </VStack>
  </Box>
);

export const ChangeLog = () => (
  // No `Section` wrapper: this is its own tab now, not one band in a long
  // scroll, so the page header above it is the only heading it needs.
  <VStack id="changelog" gap={6} align="stretch" pb={12}>
    <VStack gap={8} align="stretch">
      {RELEASES.map((release) => (
        <VStack key={release.date} gap={3} align="stretch">
          <VStack gap={1} align="stretch">
            <Text my={0} fontSize="md" fontWeight="bold" color="muted.900">
              {release.date}
            </Text>
            <Text my={0} fontSize="sm" color="muted.600" maxW="760px">
              {release.summary}
            </Text>
          </VStack>

          {release.entries.map((entry) => (
            <EntryRow
              key={entry.path + entry.name}
              entry={entry}
              date={release.date}
            />
          ))}
        </VStack>
      ))}
    </VStack>
  </VStack>
);
